import type { VideoType } from '@app-types/video'
import { AmountQuickSelect } from '@components/amount-quick-select'
import { Button } from '@components/button/button'
import { CurrencyInput } from '@components/input/currency-input'
import { ProfileCard } from '@components/profile-card'
import { Submit } from '@contentful-components/forms/submit'
import { amountUSD } from '@getstep/sdk/dist/http/models/Amount'
import { ErrorMessage } from '@hookform/error-message'
import { CustomFieldId, CustomFields } from '@lib/form/fields'
import { useIntentStage } from '@lib/store/p2p/context'
import { useProfilePaymentStore } from '@lib/store/profile-payment/profile-pay-context'
import type { PublicProfileState } from '@lib/store/PublicProfilePageStore'
import { formatAsAmount, stringToNumber } from '@lib/utils/number'
import { renderTemplate, TEXT } from '@lib/utils/text'
import classNames from 'classnames'
import get from 'lodash/get'
import { useRouter } from 'next/router'
import type { FunctionComponent } from 'react'
import { useForm, useFormState } from 'react-hook-form'

import styles from './profile-payment-card.module.scss'

const MIN_AMOUNT = 1
const MAX_AMOUNT = 1000

const ProfilePaymentCard: FunctionComponent<Props> = ({ profile, videos }) => {
    const router = useRouter()
    const store = useProfilePaymentStore()
    const stage = useIntentStage()

    const { handleSubmit, register, control, getValues, setValue, watch } =
        useForm<{
            amount: string
        }>({
            defaultValues: {
                amount: store?.amount.value,
            },
        })

    const { isSubmitting, errors, submitCount } = useFormState({ control })

    const handleQuickSelectClick = (amount: number) => {
        setValue('amount', amount.toString())
    }

    const handleAmountEditClick = () => {
        store?.moveTo(undefined, router)
    }

    const handleFormSubmit = async () => {
        const values = getValues()

        if (!values?.amount) return

        if (values?.amount) {
            store?.setValues({
                amount: amountUSD(stringToNumber(values.amount), {
                    precision: 0,
                }),
            })
        }

        store?.start(router)
    }

    const field = CustomFields[CustomFieldId.Amount]

    const latestAmount = watch('amount')

    const showQuickSelect = latestAmount === ''
    const showForm = !stage

    return (
        <div className={classNames(styles.wrapper)}>
            <form
                data-testid='pay.form'
                onSubmit={handleSubmit(handleFormSubmit)}
            >
                <ProfileCard
                    {...profile}
                    headingPrefix={TEXT.pay}
                    layout='fluid'
                    videos={videos}
                    body={
                        <div className={classNames(styles.body)}>
                            {showForm ? (
                                <>
                                    <CurrencyInput
                                        data-testid='pay.amountInput'
                                        fullWidth
                                        {...register('amount', {
                                            required: {
                                                value: true,
                                                message:
                                                    field.errors.items.find(
                                                        (e) =>
                                                            e.code ===
                                                            'required',
                                                    )?.message ?? TEXT.required,
                                            },

                                            // use custom validate function so we can verify the amount is less than the max
                                            validate: {
                                                message: (
                                                    value:
                                                        | string
                                                        | number
                                                        | any,
                                                ) => {
                                                    const amount = isNaN(value)
                                                        ? stringToNumber(value)
                                                        : value
                                                    if (
                                                        amount < MIN_AMOUNT ||
                                                        amount > MAX_AMOUNT
                                                    ) {
                                                        return renderTemplate(
                                                            TEXT.fundingAmountError,
                                                            {
                                                                min: MIN_AMOUNT,
                                                                max: MAX_AMOUNT,
                                                            },
                                                        )
                                                    }
                                                    return true
                                                },
                                            },
                                        })}
                                        autoFocus
                                        className={classNames(styles.amount)}
                                        error={
                                            <ErrorMessage
                                                errors={errors}
                                                name={CustomFieldId.Amount}
                                            />
                                        }
                                        disabled={isSubmitting}
                                        label={field.label}
                                        placeholder={field.placeholder}
                                        preset='material'
                                        size='medium'
                                        valid={!get(errors, 'amount')}
                                        validationCount={submitCount}
                                        value={latestAmount}
                                    />
                                    {showQuickSelect ? (
                                        <AmountQuickSelect
                                            onSelect={handleQuickSelectClick}
                                        />
                                    ) : (
                                        <div
                                            className={styles['submit-wrapper']}
                                        >
                                            <Submit
                                                buttonText={TEXT.continue}
                                                order='reverse'
                                                control={control}
                                                className={styles.submit}
                                            />
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div>
                                    <div
                                        className={classNames(
                                            styles['amount-text'],
                                        )}
                                    >
                                        {formatAsAmount(
                                            store?.amount.value,
                                            store?.amount.currency,
                                        )}
                                    </div>
                                    <div
                                        className={classNames(
                                            styles['edit-button'],
                                        )}
                                    >
                                        <Button
                                            size='medium'
                                            preset='secondary'
                                            onClick={handleAmountEditClick}
                                        >
                                            {TEXT.edit}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    }
                    primary={null}
                    createdAtMs=''
                />
            </form>
        </div>
    )
}

export default ProfilePaymentCard

interface Props {
    profile: PublicProfileState
    videos: VideoType[]
}
